// @flow

import type { B2bClientInterface } from './b2b-client-interface'
import type { Company } from '../../types/company'
import { buildCompany } from '../../types/company'
import { buildCardSummary } from '../../types/card-summary'
import type { CardDetails } from '../../types/card-details'
import { buildCardDetails } from '../../types/card-details'
import type { CardsPage } from '../../types/cards-page'
import { buildCardsPage } from '../../types/cards-page'
import type { TlsProductsPage } from '../../types/tls-products-page'
import { buildTlsProductsPage } from '../../types/tls-products-page'
import { buildTlsProductSummary } from '../../types/tls-product-summary'
import type {InvoicesPage} from '../../types/invoices-page'
import {buildInvoicesPage} from '../../types/invoices-page'
import {buildInvoiceSummary} from '../../types/invoice-summary'
import type { RorProductsPage } from '../../types/ror-products-page'
import { buildRorProductsPage } from '../../types/ror-products-page'
import { buildRorProductSummary } from '../../types/ror-product-summary'
import type { TripsPage } from '../../types/trips-page'
import { buildTripsPage } from '../../types/trips-page'
import { buildTripSummary } from '../../types/trip-summary'
import type {OrdersPage} from '../../types/orders-page'
import {buildOrdersPage} from '../../types/orders-page'
import {buildOrderSummary} from '../../types/order-summary'
import {ORDER_STATUS_CLOSED, ORDER_STATUS_NEW, ORDER_STATUS_PROCESSING} from '../../types/order-status'
import {
  ORDER_TYPE_NEW_CARD,
  ORDER_TYPE_NEW_CARD_WITH_PRODUCT, ORDER_TYPE_REPLACE_CARD, ORDER_TYPE_TERMINATE_CARD
} from '../../types/order-type'
import type {OrderItemsPage} from '../../types/orders-items-page'
import {buildOrderItemsPage} from '../../types/orders-items-page'
import type {OrderReplacementCardRequest} from '../../types/order-replacement-card-request'
import type {OrderTerminateCardRequest} from '../../types/order-terminate-card-request'
import type {CompanyCardsFilter} from '../../types/company-cards-filter'
import type {CompanySelfServiceStatus} from '../../types/company-self-service-status'
import {buildCompanySelfServiceStatus} from '../../types/company-self-service-status'

class MockB2bClient implements B2bClientInterface {
  async getCompany (): Promise<Company> {
    return buildCompany()
  }

  async getCompanyInvoices (
    page: number,
    pageSize: number,
    invoiceNumber: string,
    year: string,
    month: string,
    date: string,
    hasSpecification: boolean,
    orderBy: ?string,
    orderDirection: ?string
  ): Promise<InvoicesPage> {
    const invoicesPage = buildInvoicesPage()
    const invoiceSummary = buildInvoiceSummary()
    invoiceSummary.date = new Date()
    invoiceSummary.invoiceNumber = '00011023'
    invoiceSummary.invoiceType = 'ROR'
    invoiceSummary.totalAmount = {
      amount: 123.45,
      currency: 'EUR'
    }
    invoicesPage.push(invoiceSummary)
    return invoicesPage
  }

  async getCompanyCards (
    page: number,
    size: number,
    filter: CompanyCardsFilter,
    orderBy: ?string,
    orderDirection: ?string,
    includeCustomerDetails: boolean = false,
    requireCardContract: boolean = false
  ): Promise<CardsPage> {
    const cardsPage = buildCardsPage()
    const cardSummary = buildCardSummary()
    cardSummary.cardId = 13
    cardSummary.engravedId = '3528131313131313'
    cardsPage.items.push(cardSummary)

    return cardsPage
  }

  async getCompanySelfServiceStatus (): Promise<CompanySelfServiceStatus> {
    return buildCompanySelfServiceStatus(true)
  }

  async getCardDetails (cardId: number): Promise<CardDetails> {
    const cardDetails = buildCardDetails()
    cardDetails.cardId = 13
    cardDetails.activeCommercial = true
    cardDetails.engravedId = '3528987465413215'
    cardDetails.ascription = 'Tenaamstelling 0'
    cardDetails.ascription1 = 'Tenaamstelling 1'
    cardDetails.ascription2 = 'Tenaamstelling 2'
    cardDetails.contractNumber = 13
    cardDetails.expirationDate = new Date()

    return cardDetails
  }

  async postOrderReplacementCards (orderReplacementCardRequest: OrderReplacementCardRequest): Promise<void> {
  }

  async postOrderTerminateCards (orderTerminateCardRequest: OrderTerminateCardRequest): Promise<void> {
  }

  async getCardTlsProducts (
    cardId: number,
    page: number,
    pageSize: number,
    orderBy: ?string,
    orderDirection: ?string
  ): Promise<TlsProductsPage> {
    return buildTlsProductsPage(99, 2, [buildTlsProductSummary('tls product 1'), buildTlsProductSummary('tls product 2')])
  }

  async getCardRorProducts (
    cardId: number,
    page: number,
    pageSize: number,
    orderBy: ?string,
    orderDirection: ?string
  ): Promise<RorProductsPage> {
    return buildRorProductsPage(99, 2, [buildRorProductSummary('ror product 1'), buildRorProductSummary('ror product 2')])
  }

  getCardTrips (
    cardId: number,
    page: number,
    pageSize: number,
    invoiceNumber: string,
    checkinMonth: string,
    checkinDateFrom: ?Date,
    checkinDateTo: ?Date,
    orderBy: ?string,
    orderDirection: ?string
  ): Promise<TripsPage> {
    return buildTripsPage(2, 1, [buildTripSummary(), buildTripSummary()])
  }

  async patchCompanyContactDetails (company: Company): Promise<void> {
  }

  async patchCompanyAddresses (company: Company): Promise<void> {
  }

  async getOrders (
    page: number,
    pageSize: number,
    orderBy: ?string,
    orderDirection: ?string
  ): Promise<OrdersPage> {
    const orderSummary = buildOrderSummary()
    orderSummary.id = 1 + page
    orderSummary.orderDate = new Date()
    orderSummary.status = ORDER_STATUS_NEW
    orderSummary.orderType = ORDER_TYPE_NEW_CARD
    orderSummary.contractNumber = 'AA2'

    const orderSummary2 = buildOrderSummary()
    orderSummary2.id = 2 + page
    orderSummary2.orderDate = new Date()
    orderSummary2.status = ORDER_STATUS_PROCESSING
    orderSummary2.orderType = ORDER_TYPE_REPLACE_CARD
    orderSummary2.contractNumber = 'AA3'

    const orderSummary3 = buildOrderSummary()
    orderSummary3.id = 3 + page
    orderSummary3.orderDate = new Date()
    orderSummary3.status = ORDER_STATUS_CLOSED
    orderSummary3.orderType = ORDER_TYPE_NEW_CARD_WITH_PRODUCT
    orderSummary3.contractNumber = 'AA4'

    const orderSummary4 = buildOrderSummary()
    orderSummary4.id = 4 + page
    orderSummary4.orderDate = new Date()
    orderSummary4.status = ORDER_STATUS_CLOSED
    orderSummary4.orderType = ORDER_TYPE_TERMINATE_CARD
    orderSummary4.contractNumber = 'AA5'

    const orderSummaries = [orderSummary, orderSummary2, orderSummary3, orderSummary4]

    return buildOrdersPage(orderSummaries.length, 0, orderSummaries)
  }

  async getOrderItems (
    orderId: number,
    page: number,
    pageSize: number,
    orderBy: ?string,
    orderDirection: ?string
  ): Promise<OrderItemsPage> {
    return buildOrderItemsPage()
  }

  normalizeResponse (response: Object): Object {
    return response
  }

  normalizeRequest (response: Object): Object {
    return response
  }
}

export default MockB2bClient



// WEBPACK FOOTER //
// ./src/services/b2b/mock-b2b-client.js